import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email';

import dayjs from 'dayjs';
import { CustomIconButton, DefaultContainedButton, DefaultOutlinedButton } from '../../../../utils/Buttons';
import { CommonLabel } from '../../../../utils/Common';
import { ErrorUI, HttpErrorHandler } from '../../../../utils/ErrorHandler';
import { LoadingUI } from '../../../../utils/LayoutStyles';
import { CustomTextField } from '../../../../utils/StyledInputs';
import API from '../../../../api';
import { POPUP_DURATION_DELAY, POPUP_DURATION_SUCCESS, usePopup } from '../../../../context/PopupContext';
import { useUser } from '../context/UserContext';
import RenewCardChoiceDialog from '../../../../components/profile/RenewCardChoiceDialog';
import { isGdEligibleAge } from '../../../../components/registration/registrationConfig';
import { parseActiveRatesResponse } from '../../../../utils/tesseramento/tesseramentoRates';
import { createFreeLinkedGdCard as requestFreeLinkedGdCard } from '../../../../services/tesseramento/gdCardsService';
import { fetchGdEntities } from '../../../../services/tesseramento/gdEntitiesService';
import { getActiveCampaign as fetchActiveCampaign, getActivePdGdCampaigns } from '../../../../services/tesseramento/campaignsService';

export default function CardsTab({
  userID,
  canManageUser,
  canDeleteCards,
  canEditCardPaystatus,
}) {
  const { showPopup, setPopUpText } = usePopup();
  const {
    isLoading,
    errorMessage,
    birthDate,
    userEntity,
    getUserInfo,
    userInfo,
    setCards,
    setErrorMessage,
    cards,
    isBlacklistedEntity,
    canModifyUser,
    isContactUser,
  } = useUser();

  const is600Screen = useMediaQuery('(min-width: 600px)');

  // user rates
  const [userRates, setUserRates] = useState([]);
  const [totalPrice, setTotalPrice] = useState([]);
  const [activeRatesApiCalled, setActiveRatesApiCalled] = useState(false);
  const [subscriptionApiError, setSubscriptionApiError] = useState('');
  const [expiredSubscriptionApiError, setExpiredSubscriptionApiError] = useState('');
  const [disabledStates, setDisabledStates] = useState(userRates.map(() => false));
  const [activeCampaign, setActiveCampaign] = useState({});
  const [isGdAvailable, setIsGdAvailable] = useState(false);
  const [applyPaymentStatusFilter, setApplyPaymentStatusFilter] = useState(false);

  useEffect(() => {
    if (birthDate) {
      const thirtyYearsAgo = dayjs().subtract(30, 'year');
      const hasLessThan30Years = dayjs(birthDate).isAfter(dayjs(thirtyYearsAgo));
      setIsGdAvailable(hasLessThan30Years);
    }
  }, [birthDate]);

  // Get Rates Total Value
  useEffect(() => {
    let ratesSum;

    if (renewMode !== 'GD' && isGdAvailable) {
      ratesSum = userRates.reduce((sum, rate) => sum + Number(rate.price_discounted), 0);
    } else {
      ratesSum = userRates.reduce((sum, rate) => sum + Number(rate.price), 0);
    }
    setTotalPrice(ratesSum);
  }, [userRates]);

  /* ----------------------------- Create New Card ---------------------------- */
  const [openNewCard, setOpenNewCard] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);

  const [statusApiCalled, setStatusApiCalled] = useState(false);
  const [typesApiCalled, setTypesApiCalled] = useState(false);

  // --- Scelta tessera (PD / GD / GD+PD) — sprint 019 ---
  const [renewDialogOpen, setRenewDialogOpen] = useState(false);
  const [renewOptions, setRenewOptions] = useState({ pd: false, gdOnly: false, gdPlusPd: false });
  const [renewLoading, setRenewLoading] = useState(false);
  const [renewMode, setRenewMode] = useState('PD'); // 'PD' | 'GD' | 'PD_GD'
  const [gdEntitySel, setGdEntitySel] = useState(null);
  const [gdEntityList, setGdEntityList] = useState([]);
  const [needGdEntity, setNeedGdEntity] = useState(false);
  const [campaignGd, setCampaignGd] = useState({});
  const [gdBundleRates, setGdBundleRates] = useState([]);
  // Tessera PD dell'anno corrente già posseduta: se valorizzata, la GD è gratuita e collegata a essa.
  const [gdParentCardId, setGdParentCardId] = useState(null);

  // Open New Card Modal
  const handleOpenNewCard = () => {
    // Reset dello stato transitorio così un nuovo tentativo non mostra errori/tariffe precedenti.
    setSubscriptionApiError('');
    setActiveRatesApiCalled(false);
    setUserRates([]);
    if (isContactUser) {
      setSubscriptionApiError('Non è possibile tesserare un contatto');
    } else {
      getActiveCampaign();
      getPaymentStatus();
      getPaymentTypes();
    }
    setOpenNewCard(true);
  };

  // Close New Card Modal
  const handleCloseNewCard = () => {
    setOpenNewCard(false);
  };

  // --- Scelta tessera nel rinnovo (R20) ---
  // Il tesseramento al PD richiede almeno 16 anni. Lato admin si creano solo tessere PD, quindi
  // il gate vale per tutte le creazioni tessera (corrente e precedente). La creazione dell'anagrafica
  // in /admin/users resta a min 14 (un 14–15enne è un utente valido, eleggibile ai GD).
  const PD_MIN_AGE = 16;
  const isPdAgeOk = () => {
    const age = birthDate ? dayjs().diff(dayjs(birthDate), 'year') : null;
    return age === null || age >= PD_MIN_AGE;
  };
  const PD_MIN_AGE_MESSAGE = 'Il tesseramento al Partito Democratico richiede almeno 16 anni di età.';

  const openRenewDialog = () => {
    if (isContactUser) {
      setSubscriptionApiError('Non è possibile tesserare un contatto');
      setOpenNewCard(true);
      return;
    }
    if (!isPdAgeOk()) {
      showPopup('warning', PD_MIN_AGE_MESSAGE);
      return;
    }

    setRenewLoading(true);
    setRenewDialogOpen(true);
    setGdEntitySel(null);

    const age = birthDate ? dayjs().diff(dayjs(birthDate), 'year') : null;
    const gdAgeOk = isGdEligibleAge(age);
    const profileHasGdEntity = !!userInfo.entity_master_gd;
    setNeedGdEntity(!profileHasGdEntity);

    if (!profileHasGdEntity && gdAgeOk) {
      // Circoli GD filtrati per residenza/domicilio dell'utente (come in registrazione e nel
      // profilo): niente scelta libera. Residenza se disponibile, altrimenti domicilio.
      const cityId = userInfo.res_city_id || userInfo.home_city_id;
      const provinceId = userInfo.res_province_id || userInfo.home_province_id;

      if (cityId || provinceId) {
        fetchGdEntities({ cityId, provinceId })
          .then(({ entities }) => setGdEntityList(entities))
          .catch(() => setGdEntityList([]));
      } else {
        setGdEntityList([]);
      }
    }

    getActivePdGdCampaigns().then(({ pd, gd }) => {
      setCampaignGd(gd || {});

      // Multi-campagna: escludi le campagne per cui l'utente ha già una tessera (il BE blocca i doppioni).
      const pdHeld = pd && cards.some((c) => c.campaign_id === pd.id);
      // Lato admin il tesseramento GD è disabilitato: si possono creare solo tessere PD.
      setNeedGdEntity(false);
      setGdParentCardId(null);
      setRenewOptions({
        pd: !!pd && !pdHeld,
        gdOnly: false,
        gdPlusPd: false,
      });
      setRenewLoading(false);
    });
  };

  const handleRenewChoose = async (choice) => {
    // Pulisce eventuali errori del tentativo precedente prima di iniziarne uno nuovo.
    setSubscriptionApiError('');
    setActiveRatesApiCalled(false);
    // PD: flusso di creazione tessera PD classico.
    if (choice === 'PD') {
      setRenewMode('PD');
      setRenewDialogOpen(false);
      setActiveCampaign({});
      setUserRates([]);
      handleOpenNewCard();
      return;
    }

    const gdEntityId = userInfo.entity_master_gd || gdEntitySel?.id;
    if (!gdEntityId) {
      showPopup('warning', "Selezionare un'entità master GD.");
      return;
    }

    // Persist entity_master_gd sul profilo se scelta ora (contract §1B).
    if (!userInfo.entity_master_gd && gdEntitySel?.id) {
      try {
        await API.put(`/users/${userInfo.id}`, {
          entity_id: userEntity.id,
          gd_check: true,
          entity_master_gd: gdEntitySel.id,
        });
      } catch (e) {
        HttpErrorHandler(e, setPopUpText);
        showPopup(false);
        return;
      }
    }

    setRenewMode(choice);
    setRenewDialogOpen(false);
    getPaymentStatus();
    getPaymentTypes();

    const birth = dayjs(birthDate).format('YYYY/MM/DD');

    if (choice === 'GD') {
      // Utente con PD dell'anno corrente ⇒ la tessera GD è gratuita e collegata alla PD (nessun
      // dialog tariffe/pagamento). Solo chi NON ha la PD dell'anno paga la quota GD.
      if (gdParentCardId) {
        setRenewDialogOpen(false);
        await createFreeLinkedGdCard(gdParentCardId, gdEntityId);
        return;
      }
      // Campagna GD + tariffe GD (endpoint pubblico su entity_master_gd, tariffa singola).
      Promise.all([
        API.get('/active_campaign?campaign_type=GD'),
        API.get(`/active_rates/?position_id=&entity_id=${gdEntityId}&campaign_type=GD&user_birth_date=${birth}`),
      ])
        .then(([campRes, ratesRes]) => {
          setActiveCampaign(campRes.data);
          setUserRates((ratesRes.data.entity_rate || []).map((o) => o.rate));
          setActiveRatesApiCalled(true);
          setOpenNewCard(true);
        })
        .catch((error) => {
          // GD-only: se la campagna GD non ha tariffe, comunica l'errore (il dialog non si apre).
          HttpErrorHandler(error, setPopUpText);
          showPopup(false);
        });
    } else {
      // PD_GD: prepara le rate GD (importo 0) per la figlia, poi flusso PD.
      API.get(`/active_rates/?position_id=&entity_id=${gdEntityId}&campaign_type=GD&user_birth_date=${birth}`)
        .then((r) => setGdBundleRates((r.data.entity_rate || []).map((o) => ({ id: o.rate.id, price: 0 }))))
        .catch(() => setGdBundleRates([]));
      setActiveCampaign({});
      setUserRates([]);
      handleOpenNewCard();
    }
  };

  // Crea la tessera GD gratuita collegata alla PD dell'anno corrente (l'utente ha già la PD).
  // Importo 0 forzato lato server per la presenza di parent_id; nessun pagamento richiesto.
  const createFreeLinkedGdCard = async (parentCardId, gdEntityId) => {
    try {
      const res = await requestFreeLinkedGdCard({
        userId: userInfo.id,
        parentCardId,
        gdEntityId,
        birthDate,
        digitalFormat: onlineMembershipCard,
      });
      if (!res.ok) {
        showPopup('warning', 'Campagna GD non disponibile');
        return;
      }
      getUserCards();
      setPopUpText('Informazioni salvate');
      showPopup(true);
    } catch (e) {
      HttpErrorHandler(e, setPopUpText);
      showPopup(false);
    }
  };

  // Get Active Rates Api
  const getActiveRates = (campaignID = '', entityId = userInfo.entity_id) => {
    let url;
    if (campaignID.length === 0) {
      url = `admin/users/active_rates?user_id=${userID}&entity_id=${entityId}`;
    } else {
      url = `admin/users/active_rates?user_id=${userID}&entity_id=${entityId}&campaign_id=${campaignID}`;
    }

    API.get(url)
      .then((response) => {
        const activeRates = parseActiveRatesResponse(response.data);
        if (campaignID.length === 0) {
          setUserRates(activeRates);

          setActiveRatesApiCalled(true);
        } else {
          setExpiredRates(activeRates);
          setExpiredRatesApiCalled(true);
        }
      })
      .catch((error) => {
        if (campaignID.length === 0) {
          HttpErrorHandler(error, setSubscriptionApiError);
        } else {
          // Tessera precedente: senza questa gestione il dialog resterebbe in caricamento
          // infinito. Fermiamo il loading e mostriamo il messaggio (es. "Nessuna tariffa disponibile.").
          setExpiredRatesApiCalled(true);
          HttpErrorHandler(error, setExpiredSubscriptionApiError);
        }
      });
  };

  const getActiveCampaign = () => {
    if (Object.keys(activeCampaign).length === 0) {
      fetchActiveCampaign('PD')
        .then((data) => {
          setActiveCampaign(data);
          getActiveRates();
        })
        .catch((error) => {
          HttpErrorHandler(error, setSubscriptionApiError);
        });
    } else {
      getActiveRates();
    }
  };

  // get payment status data
  const getPaymentStatus = () => {
    if (Object.keys(paymentStatus).length === 0) {
      API.get('/paystatus')
        .then((response) => {
          setPaymentStatus(response.data || []);
          setStatusApiCalled(true);
        })
        .catch((error) => {
          HttpErrorHandler(error, setPopUpText);
          showPopup(false);
        });
    }
  };

  // Get PaymentTypes API
  const getPaymentTypes = () => {
    if (Object.keys(paymentTypes).length === 0) {
      if (canEditCardPaystatus) {
        API.get('/paytypes').then((response) => {
          setPaymentTypes(response.data.payment_types || []);
          setTypesApiCalled(true);
        });
      } else {
        API.get('/paytypes?for_admin=1').then((response) => {
          setPaymentTypes(response.data.payment_types || []);
          setTypesApiCalled(true);
        });
      }
    }
  };

  // create user new card
  const onCreateMembershipCard = (e) => {
    e.preventDefault();

    // GD: tariffa singola (mai sconto giovani PD).
    const singleRate = renewMode === 'GD';

    const cardRates = {
      user_id: userInfo.id,
      campaign_id: userRates[0].campaign_id,
      rates: [],
      // Tessera GD-only: paga sull'entità master GD, non sull'entità PD.
      payment_entity_id: singleRate ? userInfo.entity_master_gd || gdEntitySel?.id : userEntity.id,
      payment_online: 1,
      digital_format: onlineMembershipCard,
    };

    paymentStatus.forEach((status) => {
      if (status.type === 'W') {
        cardRates.payment_status = status.id;
      }
    });

    userRates.forEach((element) => {
      if (element.price !== 0 && element.price !== 0.0 && element.price !== '0.00') {
        cardRates.rates.push({
          id: element.id,
          price: !singleRate && isGdAvailable ? Number(element.price_discounted) : Number(element.price),
        });
      }
    });

    if (cardRates.rates.length !== 0) {
      API.post('/memcards', cardRates)
        .then((response) => {
          handleCloseNewCard();
          getUserCards();

          // Bundle GD+PD: crea la tessera GD figlia (importo 0, parent = tessera PD).
          if (renewMode === 'PD_GD' && campaignGd?.id) {
            let wStatus = null;
            paymentStatus.forEach((s) => {
              if (s.type === 'W') wStatus = s.id;
            });
            API.post('/memcards', {
              user_id: userInfo.id,
              campaign_id: campaignGd.id,
              parent_id: response.data.card.id,
              rates: gdBundleRates,
              // La tessera GD deve pagare sull'entità master GD, non sull'entità PD.
              payment_entity_id: userInfo.entity_master_gd || gdEntitySel?.id,
              payment_online: 1,
              digital_format: onlineMembershipCard,
              payment_status: wStatus,
            })
              .then(() => getUserCards())
              .catch((err) => HttpErrorHandler(err, setPopUpText));
          }
        })
        .catch((error) => {
          HttpErrorHandler(error, setPopUpText);
          showPopup(false);
        });
    } else {
      showPopup('warning', "L'importo non può essere zero");
    }
  };

  // Get User Cards
  const getUserCards = () => {
    API.get(`/users/${userInfo.id}`)
      .then((response) => {
        setCards(response.data.cards);
      })
      .catch((error) => {
        HttpErrorHandler(error, setErrorMessage);
      });
  };

  // disable rates input
  const handleDisabledInput = (index) => {
    const newDisabledStates = [...disabledStates];
    newDisabledStates[index] = !newDisabledStates[index];
    setDisabledStates(newDisabledStates);
  };
  const [otherValue, setOtherValue] = useState({});
  const isSaveEnabled = !Number.isNaN(totalPrice) && totalPrice > 0;

  // change modifiable rates value
  const handleRateChange = (index, newValue) => {
    setUserRates((prevRates) => {
      const newRates = [...prevRates];
      newRates[index].price = Number(newValue).toFixed(2).toString();
      newRates[index].price_discounted = Number(newValue).toFixed(2).toString();
      return newRates;
    });
  };

  // disable new card
  const [disableNewCard, setDisableNewCard] = useState(true);
  useEffect(() => {
    const foundCard = cards.some((card) => dayjs(card.end_date) > dayjs());

    if (foundCard) {
      setDisableNewCard(true);
    } else {
      setDisableNewCard(false);
    }
  }, [cards]);

  /* --------------------------- Create Expired Card -------------------------- */
  const [openNewExpiredCard, setOpenNewExpiredCard] = useState(false);
  const [displayExpiredOptions, setDisplayExpiredOptions] = useState(true);
  const [expiredCampaigns, setExpiredCampaigns] = useState([]);
  const [selectedExpiredCampaign, setSelectedExpiredCampaign] = useState({});
  const [expiredRates, setExpiredRates] = useState([]);
  const [disabledExpiredStates, setDisabledExpiredStates] = useState(expiredRates.map(() => false));
  const [otherExpiredValue, setOtherExpiredValue] = useState({});
  const [expiredRatesApiCalled, setExpiredRatesApiCalled] = useState(false);
  const [expiredRatesTotalPrice, setExpiredRatesTotalPrice] = useState(0);

  const handleDisabledExpiredInput = (index) => {
    const newDisabledExpiredStates = [...disabledExpiredStates];
    newDisabledExpiredStates[index] = !newDisabledExpiredStates[index];
    setDisabledExpiredStates(newDisabledExpiredStates);
  };

  // change modifiable rates value
  const handleExpiredRateChange = (index, newValue) => {
    setExpiredRates((prevRates) => {
      const newRates = [...prevRates];
      newRates[index].price = Number(newValue).toFixed(2).toString();
      newRates[index].price_discounted = Number(newValue).toFixed(2).toString();
      return newRates;
    });
  };

  const handleOpenNewExpiredCard = () => {
    setOpenNewExpiredCard(true);
    if (isContactUser) {
      setExpiredSubscriptionApiError('Non è possibile tesserare un contatto');
    } else if (!isPdAgeOk()) {
      setExpiredSubscriptionApiError(PD_MIN_AGE_MESSAGE);
    } else {
      getExpiredCampaigns();
    }
  };

  const handleCloseNewExpiredCard = () => {
    setOpenNewExpiredCard(false);
    setDisplayExpiredOptions(true);
    setExpiredRates([]);
    setSelectedExpiredCampaign({});
    setExpiredRatesTotalPrice(0);
    setExpiredRatesApiCalled(false);
    setExpiredSubscriptionApiError('');
  };

  const handleSelectedExpiredCampaign = () => {
    if (Object.keys(selectedExpiredCampaign).length === 0) {
      setPopUpText('Per proseguire è necessario selezionare una campagna');
      showPopup(false);
      return;
    }

    const isGd = selectedExpiredCampaign.campaign_type === 'GD';
    if (isGd && !userInfo.entity_master_gd) {
      setExpiredSubscriptionApiError(
        "L'utente non ha un'entità master GD: impostarla nell'anagrafica prima di creare una tessera GD precedente."
      );
      return;
    }

    setDisplayExpiredOptions(false);
    getPaymentStatus();
    getPaymentTypes();

    // GD gratuita: se l'utente ha già la PD dello stesso anno (stesso prefix), la GD sarà
    // collegata a quella tessera e a importo 0 → non servono le tariffe GD.
    const pdSameYear = isGd
      ? cards.find((c) => c.campaign_type === 'PD' && c.prefix === selectedExpiredCampaign.prefix)
      : null;
    if (pdSameYear) {
      setExpiredRates([]);
      setExpiredRatesApiCalled(true);
      return;
    }

    // GD: tariffe sull'entità master GD (tariffa singola lato BE); PD: sul Circolo.
    getActiveRates(selectedExpiredCampaign.id, isGd ? userInfo.entity_master_gd : userInfo.entity_id);
  };

  const getExpiredCampaigns = () => {
    if (expiredCampaigns.length === 0 && !isContactUser) {
      API.get('/expired_campaigns')
        .then((response) => {
          // Lato admin il tesseramento GD è disabilitato: solo campagne non-GD tra le precedenti.
          setExpiredCampaigns((response.data || []).filter((c) => c.campaign_type !== 'GD'));
        })
        .catch((error) => {
          HttpErrorHandler(error, setExpiredSubscriptionApiError);
        });
    }
  };

  useEffect(() => {
    let ratesSum;

    if (selectedExpiredCampaign?.campaign_type !== 'GD' && isGdAvailable) {
      ratesSum = expiredRates.reduce((sum, rate) => sum + Number(rate.price_discounted), 0);
    } else {
      ratesSum = expiredRates.reduce((sum, rate) => sum + Number(rate.price), 0);
    }
    setExpiredRatesTotalPrice(ratesSum);
  }, [expiredRates]);

  const handleCreateExpiredCard = (e) => {
    e.preventDefault();

    const isGd = selectedExpiredCampaign?.campaign_type === 'GD';
    // GD gratuita se l'utente ha già la PD dello stesso anno: collega a quella PD (il BE forza
    // l'importo a 0 quando la GD ha un parent_id valido). Altrimenti GD a pagamento.
    const pdSameYear = isGd
      ? cards.find((c) => c.campaign_type === 'PD' && c.prefix === selectedExpiredCampaign.prefix)
      : null;

    const data = {
      user_id: userInfo.id,
      campaign_id: selectedExpiredCampaign.id,
      rates: [],
      // Tessera GD precedente: paga sull'entità master GD (coerente con le tariffe), non sull'entità PD.
      payment_entity_id: isGd ? userInfo.entity_master_gd : userEntity.id,
      payment_online: 1,
      digital_format: true,
    };

    if (pdSameYear?.card_id) {
      data.parent_id = pdSameYear.card_id;
    }

    expiredRates.forEach((element) => {
      if (element.price !== 0 && element.price !== 0.0 && element.price !== '0.00') {
        data.rates.push({
          id: element.id,
          price:
            selectedExpiredCampaign?.campaign_type !== 'GD' && isGdAvailable
              ? Number(element.price_discounted)
              : Number(element.price),
        });
      }
    });

    paymentStatus.forEach((status) => {
      if (status.type === 'C') {
        data.payment_status = status.id;
      }
    });

    paymentTypes.forEach((type) => {
      if (type.type === 'CS') {
        data.payment_type = type.id;
      }
    });

    API.post('/memcards', data)
      .then((response) => {
        handleCloseNewExpiredCard();
        getUserCards();
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  /* -------------------------------- Edit Card ------------------------------- */
  const [openEditCard, setOpenEditCard] = useState(false);
  const [cardToEdit, setCardToEdit] = useState({});
  const [cardToEditPaymentTypeType, setCardToEditPaymentTypeType] = useState('');
  const [cardToEditPaymentType, setCardToEditPaymentType] = useState({});
  const [cardToEditPaymentStatusType, setCardToEditPaymentStatusType] = useState('');
  const [cardToEditPaymentStatus, setCardToEditPaymentStatus] = useState({});
  const [savedPaymentStatusType, setSavedPaymentStatusType] = useState('');
  const [cardToEditAuthCode, setCardToEditAuthCode] = useState('');
  const [cardToEditTransactionID, setCardToEditTransactionID] = useState('');
  const [cardToEditTerminalCode, setCardToEditTerminalCode] = useState('');
  const [onlineMembershipCard, setOnlineMembershipCard] = useState(true);

  useEffect(() => {
    if (cardToEdit && Object.keys(cardToEdit).length > 0) {
      let canEditPaymentInfo = false;

      if (!canEditCardPaystatus) {
        if (
          cardToEditPaymentTypeType !== 'C' ||
          (cardToEditPaymentTypeType === 'C' && savedPaymentStatusType === 'P')
        ) {
          canEditPaymentInfo = true;
        }
      }
      setApplyPaymentStatusFilter(canEditPaymentInfo);
    }
  }, [cardToEdit, savedPaymentStatusType, cardToEditPaymentTypeType]);

  // Open Edit Card Dialog
  const handleOpenEditCard = (card) => {
    getPaymentStatus();
    getPaymentTypes();

    setOpenEditCard(true);
    setCardToEdit(card);
    setCardToEditPaymentStatusType(card.payment_status.type);
    setSavedPaymentStatusType(card.payment_status.type);

    if (card.payment_pos_term_id) {
      setCardToEditTerminalCode(card.payment_pos_term_id);
    }

    if (card.payment_pos_auth_code) {
      setCardToEditAuthCode(card.payment_pos_auth_code);
    }

    if (card.payment_pp_trans_id) {
      setCardToEditTransactionID(card.payment_pp_trans_id);
    }

    if (card.payment_type) {
      setCardToEditPaymentTypeType(card.payment_type.type);
    }
  };

  // Close Edit Card Dialog
  const handleCloseEditCard = () => {
    setOpenEditCard(false);
    setCardToEdit({});
  };

  // Find card to edit payment status
  useEffect(() => {
    if (statusApiCalled) {
      let foundPaymentStatus;

      if (cardToEdit && Object.keys(cardToEdit).length > 0) {
        foundPaymentStatus = paymentStatus.find(
          (payment) => payment.type.toLowerCase() === cardToEditPaymentStatusType?.toLocaleLowerCase()
        );
      }

      setCardToEditPaymentStatus(foundPaymentStatus);
    }
  }, [cardToEdit, cardToEditPaymentStatusType, statusApiCalled]);

  // Find card to edit payment type
  useEffect(() => {
    if (typesApiCalled) {
      let foundPaymentType;

      if (cardToEdit && Object.keys(cardToEdit).length > 0) {
        foundPaymentType = paymentTypes.find(
          (payment) => payment.type.toLowerCase() === cardToEditPaymentTypeType.toLowerCase()
        );
      }

      setCardToEditPaymentType(foundPaymentType);
    }
  }, [cardToEdit, cardToEditPaymentTypeType, typesApiCalled]);

  // Edit Card Info
  const handleEditCard = (e) => {
    e.preventDefault();

    // Le tessere GD nascono senza payment_type: se non è stato selezionato, guida l'utente
    // invece di dereferenziare undefined (crash) o inviare una richiesta che il BE rifiuta.
    if (!cardToEditPaymentStatus?.id) {
      showPopup('warning', 'Selezionare uno stato di pagamento');
      return;
    }
    if (!cardToEditPaymentType?.id) {
      showPopup('warning', 'Selezionare un tipo di pagamento');
      return;
    }

    const updatedCardData = {
      user_id: userInfo.id,
      campaign_id: cardToEdit.campaign_id,
      payment_status: cardToEditPaymentStatus.id,
      payment_type: cardToEditPaymentType.id,
      // NB: l'entità non si modifica in edit — il BE non aggiorna payment_entity_id (viene
      // mantenuta quella di creazione), quindi non lo inviamo per non dare l'illusione contraria.
      payment_pos_auth_code: cardToEditAuthCode,
      payment_pos_term_id: cardToEditTerminalCode,
      payment_online: 1,
    };

    API.put(`/memcards/${cardToEdit.card_id}`, updatedCardData)
      .then((response) => {
        getUserInfo();
        setTimeout(() => {
          handleCloseEditCard();
        }, POPUP_DURATION_DELAY);
        setPopUpText('Informazioni salvate');
        showPopup(true);
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  const sendBankDataEmail = (e) => {
    e.preventDefault();

    const data = {
      id: cardToEdit.card_id,
      is_admin: 1,
    };

    API.get('/paymentbank')
      .then(() => {
        API.post(`/paybanksendmail`, data)
          .then(() => {
            showPopup(true, 'Email inviata con successo');
            setTimeout(() => {
              handleCloseEditCard();
            }, POPUP_DURATION_SUCCESS);
          })
          .catch((error) => {
            HttpErrorHandler(error, setPopUpText);
            showPopup(false);
          });
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  /* ------------------------------- Delete Card ------------------------------ */
  const [openDeleteCard, setOpenDeleteCard] = useState(false);
  const [cardToDelete, setCardToDelete] = useState({});

  const handleOpenDeleteCard = (card) => {
    setOpenDeleteCard(true);
    setCardToDelete(card);
  };

  const handleCloseDeleteCard = () => {
    setOpenDeleteCard(false);
    setCardToDelete({});
  };

  const handleDeleteCard = () => {
    API.delete(`/memcards/${cardToDelete.card_id}`)
      .then((response) => {
        getUserInfo();
        setTimeout(() => {
          handleCloseDeleteCard();
        }, POPUP_DURATION_DELAY);
        setPopUpText(response.data.message);
        showPopup(true);
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  return (
    <>
      {/* Loading */}
      {isLoading ? (
        LoadingUI()
      ) : errorMessage.length === 0 ? (
        <>
          <Stack
            direction={is600Screen ? 'row' : 'column'}
            justifyContent="end"
            alignItems="center"
            // sx={{ padding: '0 0 24px' }}
          >
            {/* Card action buttons */}
            <Stack direction="row" gap="10px">
              <DefaultContainedButton
                disabled={!canModifyUser || isBlacklistedEntity}
                onClick={handleOpenNewExpiredCard}
                startIcon={<AddIcon />}
              >
                Crea Tessera Precedente
              </DefaultContainedButton>

              <DefaultContainedButton
                disabled={!canModifyUser || isBlacklistedEntity}
                onClick={openRenewDialog}
                startIcon={<AddIcon />}
              >
                Crea Tessera
              </DefaultContainedButton>
            </Stack>

            <RenewCardChoiceDialog
              open={renewDialogOpen}
              onClose={() => setRenewDialogOpen(false)}
              options={renewOptions}
              onChoose={handleRenewChoose}
              loading={renewLoading}
              needGdEntity={needGdEntity}
              gdEntityValue={gdEntitySel}
              gdEntityOptions={gdEntityList}
              onGdEntityChange={setGdEntitySel}
              gdFree={!!gdParentCardId}
            />

            {/* New card dialog */}
            <Dialog fullWidth open={openNewCard} onClose={() => {}}>
              {activeRatesApiCalled && subscriptionApiError.length === 0 ? (
                <form onSubmit={onCreateMembershipCard}>
                  <DialogContent>
                    {activeCampaign?.description && (
                      <DialogTitle>{`Tesseramento ${activeCampaign.description}`}</DialogTitle>
                    )}
                    <Alert severity="error" variant="filled" sx={{ marginBottom: '8px' }}>
                      Se ricopri incarichi istituzionali (consigliere/a, assessore/a, sindaco/a, presidente,
                      parlamentare) inserisci la tua carica nella relativa sezione (“cariche”) prima di procedere al
                      tesseramento.
                    </Alert>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sx={{ overflow: 'auto' }}>
                        {userRates.length > 0 ? (
                          <>
                            <TableContainer>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell align="center">Tariffa</TableCell>
                                    <TableCell align="right">Costo</TableCell>
                                    <TableCell align="center">Valuta</TableCell>
                                    <TableCell align="center">Modificabile</TableCell>
                                  </TableRow>
                                </TableHead>

                                <TableBody>
                                  {userRates.map((rate, index) => (
                                    <TableRow hover key={rate.id}>
                                      <TableCell align="center">{rate.name}</TableCell>
                                      <TableCell sx={{ minWidth: '40px' }} align="right">
                                        {disabledStates[index] ? (
                                          otherValue[index]?.isOther ? (
                                            <CustomTextField
                                              value={otherValue[index]?.value || ''}
                                              onKeyDown={(e) => {
                                                const allowedKeys = ['Backspace', 'Tab', '.', ','];
                                                if (!allowedKeys.includes(e.key) && !/^[0-9]$/.test(e.key)) {
                                                  e.preventDefault();
                                                }
                                              }}
                                              onChange={(e) => {
                                                const inputValue = e.target.value.replace(',', '.');
                                                setOtherValue({
                                                  ...otherValue,
                                                  [index]: {
                                                    ...otherValue[index],
                                                    value: inputValue,
                                                  },
                                                });
                                                handleRateChange(index, inputValue);
                                              }}
                                              label="Importo personalizzato"
                                            />
                                          ) : (
                                            <CustomTextField
                                              customStyles={{ width: '100px' }}
                                              select
                                              onChange={(e) => {
                                                const selectedValue = e.target.value;
                                                if (selectedValue === -1) {
                                                  setOtherValue({
                                                    ...otherValue,
                                                    [index]: { isOther: true, value: '' },
                                                  });
                                                } else {
                                                  setOtherValue({
                                                    ...otherValue,
                                                    [index]: { isOther: false, value: selectedValue },
                                                  });
                                                  handleRateChange(index, selectedValue);
                                                }
                                              }}
                                              label="Importo"
                                              value={otherValue[index]?.value || ''}
                                            >
                                              <MenuItem value={0}>0</MenuItem>
                                              <MenuItem value={5}>5</MenuItem>
                                              <MenuItem value={10}>10</MenuItem>
                                              <MenuItem value={20}>20</MenuItem>
                                              <MenuItem value={50}>50</MenuItem>
                                              <MenuItem value={100}>100</MenuItem>
                                              <MenuItem value={200}>200</MenuItem>
                                              <MenuItem value={500}>500</MenuItem>
                                              <MenuItem value={-1}>Altro</MenuItem>
                                            </CustomTextField>
                                          )
                                        ) : (
                                          <Typography>{isGdAvailable ? rate.price_discounted : rate.price}</Typography>
                                        )}
                                      </TableCell>
                                      <TableCell align="center">{rate.currency}</TableCell>
                                      <TableCell align="center">
                                        {rate.has_editable === 1 ? (
                                          <DefaultOutlinedButton
                                            onClick={() => handleDisabledInput(index)}
                                            className="rate-modify-button"
                                          >
                                            {disabledStates[index] ? 'Salva' : 'Modifica'}
                                          </DefaultOutlinedButton>
                                        ) : (
                                          <DefaultOutlinedButton
                                            onClick={() => handleDisabledInput(index)}
                                            disabled
                                            className="rate-modify-button"
                                          >
                                            Fissa
                                          </DefaultOutlinedButton>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }} align="center">
                                      Totale
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontSize: '1em', fontWeight: 'bold' }}>
                                      {!Number.isNaN(totalPrice) ? totalPrice.toFixed(2) : 'Valore non valido'}
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                      EUR
                                    </TableCell>
                                    <TableCell />
                                  </TableRow>
                                </TableBody>
                              </Table>
                            </TableContainer>

                            <FormControlLabel
                              sx={{ paddingTop: '16px' }}
                              control={
                                <Switch
                                  checked={onlineMembershipCard}
                                  onChange={() => setOnlineMembershipCard(!onlineMembershipCard)}
                                />
                              }
                              label={onlineMembershipCard ? 'Tessera Online' : 'Tessera Cartacea'}
                            />
                          </>
                        ) : (
                          <Typography color="error">La campagna selezionata non ha tariffe</Typography>
                        )}
                      </Grid>
                    </Grid>
                  </DialogContent>

                  <DialogActions sx={{ paddingBottom: 3, paddingRight: 3 }}>
                    <DefaultOutlinedButton onClick={handleCloseNewCard}>Chiudi</DefaultOutlinedButton>
                    <DefaultContainedButton type="submit" disabled={!isSaveEnabled}>
                      Crea
                    </DefaultContainedButton>
                  </DialogActions>
                </form>
              ) : subscriptionApiError?.length > 0 ? (
                ErrorUI(subscriptionApiError)
              ) : (
                LoadingUI()
              )}
              {!activeRatesApiCalled && (
                <DialogActions sx={{ paddingBottom: 3, paddingRight: 3 }}>
                  <DefaultOutlinedButton onClick={handleCloseNewCard}>Chiudi</DefaultOutlinedButton>
                </DialogActions>
              )}
            </Dialog>

            {/* New expired card dialog */}
            <Dialog fullWidth open={openNewExpiredCard} onClose={() => {}}>
              {expiredCampaigns.length > 0 && expiredSubscriptionApiError.length === 0 ? (
                <>
                  <DialogContent>
                    <DialogTitle>Creazione Tesseramento Precedente</DialogTitle>

                    <Grid container spacing={3}>
                      <Grid item xs={12} sx={{ overflow: 'auto' }}>
                        {displayExpiredOptions ? (
                          <Box sx={{ width: '100%' }}>
                            <CustomTextField
                              customStyles={{ width: '80%', minWidth: '250px' }}
                              select
                              onChange={(e) => {
                                setSelectedExpiredCampaign(e.target.value);
                              }}
                              label="Seleziona Campagna"
                              value={selectedExpiredCampaign}
                            >
                              {expiredCampaigns.map((expiredCampaign) => (
                                <MenuItem key={expiredCampaign.id} value={expiredCampaign}>
                                  {expiredCampaign.description}
                                </MenuItem>
                              ))}
                            </CustomTextField>
                          </Box>
                        ) : expiredRatesApiCalled ? (
                          <>
                            <TableContainer>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell align="center">Tariffa</TableCell>
                                    <TableCell align="right">Costo</TableCell>
                                    <TableCell align="center">Valuta</TableCell>
                                    <TableCell />
                                  </TableRow>
                                </TableHead>

                                <TableBody>
                                  {expiredRates.map((rate, index) => (
                                    <TableRow key={rate.id}>
                                      <TableCell align="center">{rate.name}</TableCell>
                                      <TableCell sx={{ minWidth: '40px' }} align="right">
                                        {disabledExpiredStates[index] ? (
                                          otherExpiredValue[index]?.isOther ? (
                                            <CustomTextField
                                              value={otherExpiredValue[index]?.value || ''}
                                              onKeyDown={(e) => {
                                                const allowedKeys = ['Backspace', 'Tab', '.', ','];
                                                if (!allowedKeys.includes(e.key) && !/^[0-9]$/.test(e.key)) {
                                                  e.preventDefault();
                                                }
                                              }}
                                              onChange={(e) => {
                                                const inputValue = e.target.value.replace(',', '.');
                                                setOtherExpiredValue({
                                                  ...otherExpiredValue,
                                                  [index]: {
                                                    ...otherExpiredValue[index],
                                                    value: inputValue,
                                                  },
                                                });
                                                handleExpiredRateChange(index, inputValue);
                                              }}
                                              label="Importo personalizzato"
                                            />
                                          ) : (
                                            <CustomTextField
                                              customStyles={{ width: '100px' }}
                                              select
                                              onChange={(e) => {
                                                const selectedValue = e.target.value;
                                                if (selectedValue === -1) {
                                                  setOtherExpiredValue({
                                                    ...otherExpiredValue,
                                                    [index]: { isOther: true, value: '' },
                                                  });
                                                } else {
                                                  setOtherExpiredValue({
                                                    ...otherExpiredValue,
                                                    [index]: { isOther: false, value: selectedValue },
                                                  });
                                                  handleExpiredRateChange(index, selectedValue);
                                                }
                                              }}
                                              label="Importo"
                                              value={otherExpiredValue[index]?.value || ''}
                                            >
                                              <MenuItem value={0}>0</MenuItem>
                                              <MenuItem value={5}>5</MenuItem>
                                              <MenuItem value={10}>10</MenuItem>
                                              <MenuItem value={20}>20</MenuItem>
                                              <MenuItem value={50}>50</MenuItem>
                                              <MenuItem value={100}>100</MenuItem>
                                              <MenuItem value={200}>200</MenuItem>
                                              <MenuItem value={500}>500</MenuItem>
                                              <MenuItem value={-1}>Altro</MenuItem>
                                            </CustomTextField>
                                          )
                                        ) : (
                                          <Typography>{isGdAvailable ? rate.price_discounted : rate.price}</Typography>
                                        )}
                                      </TableCell>
                                      <TableCell align="center">{rate.currency}</TableCell>
                                      <TableCell align="center">
                                        {rate.has_editable === 1 ? (
                                          <DefaultOutlinedButton
                                            onClick={() => handleDisabledExpiredInput(index)}
                                            className="rate-modify-button"
                                          >
                                            {disabledExpiredStates[index] ? 'Salva' : 'Modifica'}
                                          </DefaultOutlinedButton>
                                        ) : (
                                          <DefaultOutlinedButton
                                            onClick={() => handleDisabledExpiredInput(index)}
                                            disabled
                                            className="rate-modify-button"
                                          >
                                            Fissa
                                          </DefaultOutlinedButton>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  ))}

                                  {/* Rates */}
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }} align="center">
                                      Totale
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontSize: '1em', fontWeight: 'bold' }}>
                                      {!Number.isNaN(expiredRatesTotalPrice)
                                        ? expiredRatesTotalPrice.toFixed(2)
                                        : 'Valore non valido'}
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                      EUR
                                    </TableCell>
                                    <TableCell />
                                  </TableRow>
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </>
                        ) : (
                          LoadingUI()
                        )}
                      </Grid>
                    </Grid>
                  </DialogContent>

                  <DialogActions sx={{ paddingBottom: 3, paddingRight: 3 }}>
                    {displayExpiredOptions ? (
                      <>
                        <DefaultOutlinedButton onClick={handleCloseNewExpiredCard}>Chiudi</DefaultOutlinedButton>
                        <DefaultContainedButton onClick={handleSelectedExpiredCampaign}>
                          Seleziona
                        </DefaultContainedButton>
                      </>
                    ) : (
                      <>
                        <DefaultOutlinedButton onClick={handleCloseNewExpiredCard}>Chiudi</DefaultOutlinedButton>
                        <DefaultContainedButton onClick={handleCreateExpiredCard}>Crea</DefaultContainedButton>
                      </>
                    )}
                  </DialogActions>
                </>
              ) : expiredSubscriptionApiError?.length > 0 ? (
                ErrorUI(expiredSubscriptionApiError)
              ) : (
                LoadingUI()
              )}
              {(expiredCampaigns.length === 0 || expiredSubscriptionApiError?.length > 0) && (
                <DialogActions sx={{ paddingBottom: 3, paddingRight: 3 }}>
                  <DefaultOutlinedButton onClick={handleCloseNewExpiredCard}>Chiudi</DefaultOutlinedButton>
                </DialogActions>
              )}
            </Dialog>
          </Stack>

          {isBlacklistedEntity ? (
            <Alert variant="filled" severity="error" sx={{ marginTop: '24px' }}>
              Al momento il tesseramento è stato disabilitato.
            </Alert>
          ) : (
            <Alert variant="filled" severity="error" sx={{ marginTop: '24px' }}>
              Se ricopri incarichi istituzionali (consigliere/a, assessore/a, sindaco/a, presidente, parlamentare)
              inserisci la tua carica nella relativa sezione (“cariche”) prima di procedere al tesseramento.{' '}
            </Alert>
          )}

          <Card sx={{ padding: '24px', marginTop: '10px' }}>
            {/* Table */}
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell>Campagna</TableCell>
                    <TableCell>Stato Tessera</TableCell>
                    <TableCell>Entità</TableCell>
                    <TableCell>Data Tesseramento</TableCell>
                    <TableCell>Numero Tessera</TableCell>
                    <TableCell>Metodo di Pagamento</TableCell>
                    <TableCell>Quota Tessera</TableCell>
                    <TableCell>Stato Pagamento</TableCell>
                  </TableRow>
                </TableHead>
                {cards.length > 0 ? (
                  <TableBody>
                    {/* Edit Card */}
                    <Dialog open={openEditCard} fullWidth onClose={() => {}}>
                      <DialogTitle sx={{ textAlign: 'center' }}>Pagamento Tessera</DialogTitle>
                      {statusApiCalled && typesApiCalled ? (
                        <>
                          <form onSubmit={handleEditCard}>
                            <DialogContent>
                              <Grid container spacing={3}>
                                {/* Campaign Name */}
                                <Grid item xs={12}>
                                  <CustomTextField
                                    disabled
                                    name="name"
                                    label="Nome Campagna"
                                    defaultValue={cardToEdit.campaign_name}
                                  />
                                </Grid>

                                {/* Entity name */}
                                <Grid item xs={12}>
                                  <CustomTextField
                                    disabled
                                    name="entity"
                                    label="Entità"
                                    defaultValue={cardToEdit.payment_entity}
                                  />
                                </Grid>

                                <Grid item xs={12}>
                                  <CustomTextField
                                    disabled
                                    name="date"
                                    label="Data Tesseramento"
                                    defaultValue={dayjs(cardToEdit.creation_date).format('DD/MM/YYYY')}
                                  />
                                </Grid>

                                {/* Card payment type */}
                                <Grid item xs={12}>
                                  <FormControl color="secondary" sx={{ width: '100%' }}>
                                    <InputLabel>Tipo Pagamento</InputLabel>
                                    <Select
                                      sx={{ width: '100%' }}
                                      value={cardToEditPaymentTypeType}
                                      label="Tipo Pagamento"
                                      disabled={cardToEditPaymentStatusType === 'P' && !canEditCardPaystatus}
                                      onChange={(e) => setCardToEditPaymentTypeType(e.target.value)}
                                    >
                                      {Object.keys(paymentTypes).length > 0 &&
                                        (Array.isArray(paymentTypes) ? paymentTypes : []).map((paymentType) => (
                                          <MenuItem key={paymentType.id} value={paymentType.type}>
                                            {paymentType.name}
                                          </MenuItem>
                                        ))}
                                    </Select>
                                  </FormControl>
                                </Grid>

                                {/* Card payment status */}
                                <Grid item xs={12}>
                                  <FormControl color="secondary" sx={{ width: '100%' }}>
                                    <InputLabel>Stato Pagamento</InputLabel>
                                    <Select
                                      sx={{ width: '100%' }}
                                      value={cardToEditPaymentStatusType}
                                      disabled={applyPaymentStatusFilter}
                                      label="Stato Pagamento"
                                      onChange={(e) => setCardToEditPaymentStatusType(e.target.value)}
                                    >
                                      {Object.keys(paymentStatus).length > 0 &&
                                        paymentStatus
                                          .filter((ps) => ps.type !== 'C')
                                          .map((ps) => (
                                            <MenuItem key={ps.id} value={ps.type}>
                                              {ps.name}
                                            </MenuItem>
                                          ))}
                                    </Select>
                                  </FormControl>
                                </Grid>

                                {cardToEditPaymentTypeType === 'BB' && (
                                  <Grid item xs={12}>
                                    <DefaultOutlinedButton onClick={sendBankDataEmail} startIcon={<EmailIcon />}>
                                      Invia coordinate bancarie
                                    </DefaultOutlinedButton>
                                  </Grid>
                                )}

                                {cardToEditPaymentTypeType === 'PB' && (
                                  <>
                                    <Grid item xs={12}>
                                      <CustomTextField
                                        name="terminalID"
                                        label="ID Terminale"
                                        value={cardToEditTerminalCode}
                                        onChange={(e) => setCardToEditTerminalCode(e.currentTarget.value)}
                                      />
                                    </Grid>

                                    <Grid item xs={12}>
                                      <CustomTextField
                                        name="authCode"
                                        label="Codice Autorizzazione"
                                        value={cardToEditAuthCode}
                                        onChange={(e) => setCardToEditAuthCode(e.currentTarget.value)}
                                      />
                                    </Grid>
                                  </>
                                )}
                              </Grid>

                            </DialogContent>
                            <DialogActions>
                              <DefaultOutlinedButton onClick={handleCloseEditCard}>Chiudi</DefaultOutlinedButton>
                              <DefaultContainedButton type="submit">Salva</DefaultContainedButton>
                            </DialogActions>
                          </form>
                        </>
                      ) : (
                        LoadingUI()
                      )}
                    </Dialog>

                    {/* Delete Card */}
                    <Dialog open={openDeleteCard} fullWidth onClose={() => {}}>
                      <DialogTitle sx={{ textAlign: 'center' }}>Eliminazione Tessera</DialogTitle>

                      <DialogContent>
                        <Typography variant="span">
                          Sei sicuro di voler eliminare la tessera numero {cardToDelete.prefix}
                          {cardToDelete.card_number}?
                        </Typography>
                      </DialogContent>
                      <DialogActions>
                        <DefaultOutlinedButton onClick={handleCloseDeleteCard}>Chiudi</DefaultOutlinedButton>
                        <DefaultContainedButton onClick={handleDeleteCard}>Elimina</DefaultContainedButton>
                      </DialogActions>
                    </Dialog>

                    {cards.map((card) => (
                      <TableRow hover key={card.card_id}>
                        <TableCell>
                          <Stack
                            sx={{
                              display: canEditCardPaystatus
                                ? 'flex'
                                : !canEditCardPaystatus && card.payment_status.type === 'P'
                                ? 'none'
                                : 'flex',
                            }}
                            direction="row"
                            alignItems="center"
                            justifyContent="end"
                          >
                            {/* Edit Card */}
                            <Tooltip title="Aggiorna Tessera" arrow placement="top">
                              <Box>
                                <CustomIconButton disabled={!canModifyUser} onClick={() => handleOpenEditCard(card)}>
                                  <EditIcon />
                                </CustomIconButton>
                              </Box>
                            </Tooltip>

                            {/* Delete Card */}
                            {canDeleteCards && (
                              <Tooltip title="Elimina Tessera" arrow placement="top">
                                <Box>
                                  <CustomIconButton onClick={() => handleOpenDeleteCard(card)}>
                                    <DeleteIcon />
                                  </CustomIconButton>
                                </Box>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>{card.campaign_name}</TableCell>
                        <TableCell>
                          <CommonLabel
                            textColor={dayjs(card.end_date) > dayjs(new Date()) ? '#118d57' : '#b71d18'}
                            bgColor={dayjs(card.end_date) > dayjs(new Date()) ? '#d3efdf' : '#f7ddd8'}
                            text={dayjs(card.end_date) > dayjs(new Date()) ? 'Attiva' : 'Scaduta'}
                          />
                        </TableCell>
                        <TableCell>{card.payment_entity}</TableCell>
                        <TableCell>{dayjs(card.creation_date || card.created_at).format('DD/MM/YYYY')}</TableCell>
                        <TableCell>
                          {card.card_code || `${card.campaign_type || ''}${card.prefix}${card.card_number}`}
                        </TableCell>
                        <TableCell>{card.payment_type ? card.payment_type.name : '//'}</TableCell>
                        <TableCell>{`${card.price?.toFixed(2)}€`}</TableCell>
                        <TableCell>
                          <CommonLabel
                            textColor={card.payment_status.type === 'P' ? '#118d57' : '#b71d18'}
                            bgColor={card.payment_status.type === 'P' ? '#d3efdf' : '#f7ddd8'}
                            text={card.payment_status.name}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                ) : (
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        Nessuna tessera disponibile
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Card>
        </>
      ) : (
        // Error handler
        ErrorUI(errorMessage)
      )}
    </>
  );
}
